import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../Common/Configurations/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import apiService from "../../Common/Configurations/APIService";
import config from "../../Common/Configurations/APIConfig";
import DefaultData from "../../CompanyData/DefaultData"; // Import DefaultData
import Header from "../../Common/Layout/Header";
import Modal from "react-bootstrap/Modal";
import DeleteAlert from "../../Common/Layout/DeleteAlert";
import { format } from "date-fns";
import currencyFlags from "../../Data/CurrencyFlags.json";
import AccountTabs from "../Accounts/AccountTabs";

const GlobalTax = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // State for taxes list
  const [taxes, setTaxes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Countries state
  const [countries, setCountries] = useState([]);

  // Modal states
  const [editShow, setEditShow] = useState(false);
  const [editTax, setEditTax] = useState({
    companyId: null,
    taxName: "",
    taxType: "",
    countryCode: "",
    stateCode: "",
    rate: 0,
    isCompound: false,
    isActive: true,
    effectiveFrom: new Date().toISOString().slice(0, 16),
    effectiveTo: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 16),
  });
  const [editBtnLoading, setEditBtnLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Tax types options
  const taxTypes = ["GST", "VAT", "Sales Tax", "Service Tax", "CST", "IGST", "CGST", "SGST", "UTGST", "TDS", "Other"];


  // Load countries from currencyFlags
  useEffect(() => {
    if (currencyFlags && Array.isArray(currencyFlags)) {
      const sortedCountries = [...currencyFlags].sort((a, b) =>
        a.country.localeCompare(b.country)
      );
      setCountries(sortedCountries);
    }
  }, []);

  // Get all taxes
  const getTaxes = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const response = await apiService.getAll(config.GETALLGLOBALTAXES);
      const data = response.data || response || [];
      setTaxes(data);
    } catch (error) {
      console.error("Error fetching global taxes:", error);
      setTaxes([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!authLoading && user) {
      getTaxes();
    }
  }, [user, authLoading, getTaxes]);

  // Handle Add Tax
  const handleAddTax = () => {
    setEditTax({
      globalTaxId: null,
      companyId: DefaultData.DEFAULTCOMPANYID, // Set default company ID
      taxName: "",
      taxType: "",
      countryCode: "",
      stateCode: "",
      rate: 0,
      isCompound: false,
      isActive: true,
      effectiveFrom: new Date().toISOString().slice(0, 16),
      effectiveTo: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
        .toISOString()
        .slice(0, 16),
    });
    setFormErrors({});
    setEditShow(true);
  };

  // Handle Edit Tax
  const handleEditClick = (tax) => {
    const editData = {
      ...tax,
      globalTaxId: tax.globalTaxId,
      effectiveFrom: tax.effectiveFrom
        ? new Date(tax.effectiveFrom).toISOString().slice(0, 16)
        : new Date().toISOString().slice(0, 16),
      effectiveTo: tax.effectiveTo
        ? new Date(tax.effectiveTo).toISOString().slice(0, 16)
        : new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
          .toISOString()
          .slice(0, 16),
    };
    setEditTax(editData);
    setFormErrors({});
    setEditShow(true);
  };

  // Close edit modal
  const handleEditClose = () => {
    setEditShow(false);
    setEditTax({
      globalTaxId: null,
      companyId: DefaultData.DEFAULTCOMPANYID, // Keep default company ID
      taxName: "",
      taxType: "",
      countryCode: "",
      stateCode: "",
      rate: 0,
      isCompound: false,
      isActive: true,
      effectiveFrom: "",
      effectiveTo: "",
    });
    setFormErrors({});
    setEditBtnLoading(false);
  };

  // Handle input change
  const handleEditInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditTax({
      ...editTax,
      [name]: type === "checkbox" ? checked : value,
    });

    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: null });
    }
  };

  // Handle select change for dropdowns
  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    // Convert string to boolean for isActive and isCompound
    if (name === "isActive" || name === "isCompound") {
      setEditTax({
        ...editTax,
        [name]: value === "true",
      });
    } else {
      setEditTax({
        ...editTax,
        [name]: value,
      });
    }

    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: null });
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!editTax.taxName || editTax.taxName.trim() === "") {
      errors.taxName = "Tax name is required";
    }

    if (!editTax.taxType) {
      errors.taxType = "Tax type is required";
    }

    if (!editTax.countryCode) {
      errors.countryCode = "Country is required";
    }

    if (editTax.rate === undefined || editTax.rate === null || editTax.rate < 0) {
      errors.rate = "Rate must be a valid positive number";
    }

    if (!editTax.effectiveFrom) {
      errors.effectiveFrom = "Effective from date is required";
    }

    if (!editTax.effectiveTo) {
      errors.effectiveTo = "Effective to date is required";
    }

    if (editTax.effectiveFrom && editTax.effectiveTo) {
      const fromDate = new Date(editTax.effectiveFrom);
      const toDate = new Date(editTax.effectiveTo);
      if (toDate <= fromDate) {
        errors.effectiveTo = "Effective to date must be after effective from date";
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle Save Tax
  const handleSaveTax = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setEditBtnLoading(true);
    try {
      // Prepare the data - use DefaultData.DEFAULTCOMPANYID if companyId is null or empty
      const companyId = editTax.companyId || DefaultData.DEFAULTCOMPANYID;

      const taxData = {
        companyId: companyId,
        taxName: editTax.taxName.trim(),
        taxType: editTax.taxType,
        countryCode: editTax.countryCode,
        stateCode: editTax.stateCode || "",
        rate: parseFloat(editTax.rate),
        isCompound: editTax.isCompound,
        isActive: editTax.isActive,
        effectiveFrom: new Date(editTax.effectiveFrom).toISOString(),
        effectiveTo: new Date(editTax.effectiveTo).toISOString(),
      };

      // Only add globalTaxId for update operations
      if (editTax.globalTaxId) {
        taxData.globalTaxId = editTax.globalTaxId;
      }

      let response;
      if (editTax.globalTaxId) {
        // Update existing tax
        response = await apiService.update(
          config.UPDATEGLOBALTAX,
          taxData
        );
      } else {
        // Create new tax
        response = await apiService.create(
          config.CREATEGLOBALTAX,
          taxData
        );
      }

      if (response && (response.success || response === true)) {
        await getTaxes();
        handleEditClose();
      } else {
        console.error("Failed to save tax");
        setFormErrors({ submit: "Failed to save tax. Please try again." });
      }
    } catch (error) {
      console.error("Error saving tax:", error);
      setFormErrors({
        submit: error.response?.data?.title ||
          error.response?.data?.message ||
          "An error occurred while saving"
      });
    } finally {
      setEditBtnLoading(false);
    }
  };

  // Delete functionality
  const handleOpenDeleteModal = (taxId) => {
    setItemToDelete(taxId);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const handleDelete = async () => {
    if (itemToDelete) {
      try {
        await apiService.remove(
          config.DELETEGLOBALTAX,
          itemToDelete
        );
        await getTaxes();
      } catch (error) {
        console.error("Error deleting tax:", error);
      }
      handleCloseDeleteModal();
    }
  };

  // Get country name from code
  const getCountryName = (code) => {
    const country = countries.find((c) => c.countryCode === code);
    return country ? country.country : code;
  };

  // Get country flag from code
  const getCountryFlag = (code) => {
    const country = countries.find((c) => c.countryCode === code);
    return country ? country.flag : null;
  };

  // Format date as "12-Aug-2026"
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return format(new Date(dateString), "dd-MMM-yyyy");
    } catch {
      return "-";
    }
  };

  if (authLoading) return <p>Loading...</p>;
  if (!user) {
    navigate("/");
    return null;
  }

  return (
    <>
      <Header />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="card">
              <div className="p-2">
                <AccountTabs pageName="GLOBALTAXES" />
              </div>
              <div className="card-body">
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h5 className="card-title mb-0">
                        <i className="ri-global-line me-2"></i>
                        Global Tax Management
                      </h5>
                      <button className="btn btn-primary" onClick={handleAddTax}>
                        <i className="ri-add-line me-2"></i>
                        Add Tax
                      </button>
                    </div>
                  </div>
                </div>

                {/* Table Section */}
                <div className="table-responsive table-card">
                  <table className="table align-middle table-hover">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: "50px" }}>#</th>
                        <th>Tax Name</th>
                        <th>Type</th>
                        <th>Country</th>
                        <th>State</th>
                        <th>Rate (%)</th>
                        <th>Compound</th>
                        <th>Effective From</th>
                        <th>Effective To</th>
                        <th>Status</th>
                        <th style={{ width: "120px" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="11" className="text-center py-4">
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-2">Loading taxes...</p>
                          </td>
                        </tr>
                      ) : taxes.length > 0 ? (
                        taxes.map((tax, index) => (
                          <tr key={tax.globalTaxId}>
                            <td>{index + 1}</td>
                            <td>
                              <strong>{tax.taxName}</strong>
                            </td>
                            <td>
                              <span className="badge bg-info">{tax.taxType}</span>
                            </td>
                            <td>
                              {getCountryFlag(tax.countryCode) && (
                                <img
                                  src={getCountryFlag(tax.countryCode)}
                                  alt={tax.countryCode}
                                  style={{
                                    width: "20px",
                                    height: "15px",
                                    marginRight: "6px",
                                    verticalAlign: "middle",
                                  }}
                                />
                              )}
                              {getCountryName(tax.countryCode)}
                            </td>
                            <td>{tax.stateCode || "-"}</td>
                            <td>
                              <strong>{tax.rate}%</strong>
                            </td>
                            <td>
                              <span
                                className={`badge ${tax.isCompound ? "bg-warning" : "bg-secondary"}`}
                              >
                                {tax.isCompound ? "Yes" : "No"}
                              </span>
                            </td>
                            <td>{formatDate(tax.effectiveFrom)}</td>
                            <td>{formatDate(tax.effectiveTo)}</td>
                            <td>
                              <span
                                className={`badge ${tax.isActive ? "bg-success" : "bg-danger"}`}
                              >
                                {tax.isActive ? "Active" : "Inactive"}
                              </span>
                            </td>
                            <td>
                              <ul className="list-inline hstack gap-2 mb-0">
                                <li
                                  className="list-inline-item"
                                  data-bs-toggle="tooltip"
                                  data-bs-trigger="hover"
                                  data-bs-placement="top"
                                  title="Edit"
                                >
                                  <Link
                                    className="edit-item-btn"
                                    onClick={() => handleEditClick(tax)}
                                  >
                                    <i className="ri-pencil-fill align-bottom text-muted" />
                                  </Link>
                                </li>
                                <li
                                  className="list-inline-item"
                                  data-bs-toggle="tooltip"
                                  data-bs-trigger="hover"
                                  data-bs-placement="top"
                                  title="Delete"
                                >
                                  <Link
                                    className="remove-item-btn"
                                    onClick={() =>
                                      handleOpenDeleteModal(tax.globalTaxId)
                                    }
                                  >
                                    <i className="ri-delete-bin-fill align-bottom text-muted" />
                                  </Link>
                                </li>
                              </ul>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="11" className="text-center py-4">
                            <i className="ri-inbox-line fs-1 text-muted d-block mb-2"></i>
                            No global taxes found.
                            <br />

                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Total count */}
                <div className="row mt-3">
                  <div className="col-12">
                    <span className="text-muted">
                      Total Taxes: {taxes.length}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Add/Edit Tax Modal */}
        <Modal
          show={editShow}
          onHide={handleEditClose}
          size="lg"
          centered
          scrollable
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {editTax.globalTaxId ? (
                <>
                  <i className="ri-edit-line me-2 text-primary"></i>
                  Edit Tax - {editTax.taxName}
                </>
              ) : (
                <>
                  <i className="ri-add-line me-2 text-primary"></i>
                  Add New Global Tax
                </>
              )}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
            <form onSubmit={handleSaveTax}>
              <div className="container-fluid">
                {/* Error message */}
                {formErrors.submit && (
                  <div className="alert alert-danger alert-dismissible fade show mb-3">
                    <i className="ri-error-warning-line me-2"></i>
                    {formErrors.submit}
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setFormErrors({ ...formErrors, submit: null })}
                    ></button>
                  </div>
                )}

                <div className="row">
                  {/* Tax Name */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-bold">
                      Tax Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${formErrors.taxName ? "is-invalid" : ""}`}
                      name="taxName"
                      value={editTax.taxName}
                      onChange={handleEditInputChange}
                      placeholder="e.g., GST 18%, VAT 5%"
                      maxLength={100}
                    />
                    {formErrors.taxName && (
                      <div className="invalid-feedback">{formErrors.taxName}</div>
                    )}
                  </div>

                  {/* Tax Type */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-bold">
                      Tax Type <span className="text-danger">*</span>
                    </label>
                    <select
                      className={`form-select ${formErrors.taxType ? "is-invalid" : ""}`}
                      name="taxType"
                      value={editTax.taxType}
                      onChange={handleSelectChange}
                    >
                      <option value="">Select Tax Type</option>
                      {taxTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                    {formErrors.taxType && (
                      <div className="invalid-feedback">{formErrors.taxType}</div>
                    )}
                  </div>

                  {/* Country */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-bold">
                      Country <span className="text-danger">*</span>
                    </label>
                    <select
                      className={`form-select ${formErrors.countryCode ? "is-invalid" : ""}`}
                      name="countryCode"
                      value={editTax.countryCode}
                      onChange={handleSelectChange}
                    >
                      <option value="">Select Country</option>
                      {countries.map((country) => (
                        <option key={country.countryCode} value={country.countryCode}>
                          {country.flag && (
                            <img
                              src={country.flag}
                              alt={country.country}
                              style={{ width: "20px", height: "15px", marginRight: "8px" }}
                            />
                          )}
                          {country.country} - {country.countryCode}
                        </option>
                      ))}
                    </select>
                    {formErrors.countryCode && (
                      <div className="invalid-feedback">{formErrors.countryCode}</div>
                    )}
                  </div>

                  {/* State Code */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-bold">State Code</label>
                    <input
                      type="text"
                      className="form-control"
                      name="stateCode"
                      value={editTax.stateCode || ""}
                      onChange={handleEditInputChange}
                      placeholder="e.g., TS, CA, NY"
                      maxLength={10}
                    />
                    <small className="text-muted">
                      Optional - Leave empty for country-wide tax
                    </small>
                  </div>

                  {/* Rate */}
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold">
                      Rate (%) <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      className={`form-control ${formErrors.rate ? "is-invalid" : ""}`}
                      name="rate"
                      value={editTax.rate}
                      onChange={handleEditInputChange}
                      placeholder="e.g., 18"
                      min="0"
                      max="100"
                      step="0.01"
                    />
                    {formErrors.rate && (
                      <div className="invalid-feedback">{formErrors.rate}</div>
                    )}
                  </div>

                  {/* Compound Tax - Dropdown */}
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold">
                      Compound Tax <span className="text-danger">*</span>
                    </label>
                    <select
                      className={`form-select ${formErrors.isCompound ? "is-invalid" : ""}`}
                      name="isCompound"
                      value={editTax.isCompound.toString()}
                      onChange={handleSelectChange}
                    >
                      <option value="false">No</option>
                      <option value="true">Yes</option>
                    </select>
                    <small className="text-muted d-block">
                      Tax-on-tax calculation flag
                    </small>
                    {formErrors.isCompound && (
                      <div className="invalid-feedback">{formErrors.isCompound}</div>
                    )}
                  </div>

                  {/* Status - Dropdown */}
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold">
                      Status <span className="text-danger">*</span>
                    </label>
                    <select
                      className={`form-select ${formErrors.isActive ? "is-invalid" : ""}`}
                      name="isActive"
                      value={editTax.isActive.toString()}
                      onChange={handleSelectChange}
                    >
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                    {formErrors.isActive && (
                      <div className="invalid-feedback">{formErrors.isActive}</div>
                    )}
                  </div>

                  {/* Effective From */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-bold">
                      Effective From <span className="text-danger">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      className={`form-control ${formErrors.effectiveFrom ? "is-invalid" : ""}`}
                      name="effectiveFrom"
                      value={editTax.effectiveFrom}
                      onChange={handleEditInputChange}
                    />
                    {formErrors.effectiveFrom && (
                      <div className="invalid-feedback">{formErrors.effectiveFrom}</div>
                    )}
                  </div>

                  {/* Effective To */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-bold">
                      Effective To <span className="text-danger">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      className={`form-control ${formErrors.effectiveTo ? "is-invalid" : ""}`}
                      name="effectiveTo"
                      value={editTax.effectiveTo}
                      onChange={handleEditInputChange}
                    />
                    {formErrors.effectiveTo && (
                      <div className="invalid-feedback">{formErrors.effectiveTo}</div>
                    )}
                  </div>
                </div>


                {/* Form Actions */}
                <div className="row mt-3">
                  <div className="col-12">
                    <hr />
                    <div className="d-flex justify-content-end gap-2">
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={handleEditClose}
                        disabled={editBtnLoading}
                      >
                        <i className="ri-close-line me-1"></i>
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={editBtnLoading}
                      >
                        {editBtnLoading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            {editTax.globalTaxId ? "Updating..." : "Creating..."}
                          </>
                        ) : (
                          <>
                            <i className={`ri-${editTax.globalTaxId ? "save" : "add"}-line me-1`}></i>
                            {editTax.globalTaxId ? "Update Tax" : "Create Tax"}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </Modal.Body>
        </Modal>

        {/* Delete Confirmation Modal */}
        <DeleteAlert
          show={showDeleteModal}
          handleClose={handleCloseDeleteModal}
          handleDelete={handleDelete}
          title="Delete Global Tax"
          message="Are you sure you want to delete this global tax? This action cannot be undone."
        />
      </div>
    </>
  );
};

export default GlobalTax;